/**
 * Empty State Component
 * Reusable component for displaying empty states with Lottie animations
 */

import { Package, Sparkles } from 'lucide-react';
import React from 'react';
import documentsAnimation from '../../assets/lottie/documents_scan.json';
import { LottieAnimation } from './LottieAnimation';

interface EmptyStateProps {
  title: string;
  description: string;
  icon?: 'package' | 'upload' | 'lottie';
  lottieData?: unknown;
  primaryAction?: {
    dataTourId?: string;
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  secondaryAction?: {
    dataTourId?: string;
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon = 'package',
  lottieData,
  primaryAction,
  secondaryAction,
  className = '',
}) => {
  const renderIcon = () => {
    if (icon === 'lottie' && lottieData) {
      return (
        <div className="w-32 h-32 mx-auto mb-4">
          <LottieAnimation animationData={lottieData} />
        </div>
      );
    }

    if (icon === 'upload') {
      return (
        <div className="w-32 h-32 mx-auto mb-4">
          <LottieAnimation animationData={documentsAnimation} />
        </div>
      );
    }

    return <Package size={48} className="mx-auto mb-4 text-gray-400" />;
  };

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 p-12 text-center ${className}`}
    >
      {renderIcon()}
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">{description}</p>

      <div className="flex items-center justify-center gap-3">
        {primaryAction && (
          <button
            type="button"
            onClick={primaryAction.onClick}
            data-onboarding-tour={primaryAction.dataTourId}
            className="inline-flex items-center gap-2 px-4 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors"
          >
            {primaryAction.icon}
            <span>{primaryAction.label}</span>
          </button>
        )}
        {secondaryAction && (
          <button
            type="button"
            onClick={secondaryAction.onClick}
            data-onboarding-tour={secondaryAction.dataTourId}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {secondaryAction.icon || <Sparkles size={18} />}
            <span>{secondaryAction.label}</span>
          </button>
        )}
      </div>
    </div>
  );
};
